import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { verticalsApi, ApiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
type Props = {
  verticalId: string;
  onSaved?: () => void;
};

function toBool(v: unknown, defaultTrue = true): boolean {
  if (v === false) return false;
  if (v === true) return true;
  return defaultTrue;
}

/**
 * Sprint 2 — edit Firestore vertical gates + goals via API.
 */
export default function VerticalControlsPanel({ verticalId, onSaved }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const [scan, setScan] = useState(true);
  const [signals, setSignals] = useState(true);
  const [live, setLive] = useState(true);
  const [engine, setEngine] = useState(false);

  const [maxLoss, setMaxLoss] = useState('');
  const [budgetPct, setBudgetPct] = useState('');
  const [profitPct, setProfitPct] = useState('');
  const [notes, setNotes] = useState('');

  const [wrSchedule, setWrSchedule] = useState(false);
  const [wrTime, setWrTime] = useState('06:30');
  const [wrTz, setWrTz] = useState('America/New_York');
  const [wrLastRun, setWrLastRun] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [cfgRes, goalsRes] = await Promise.all([
        verticalsApi.getConfig(verticalId),
        verticalsApi.getGoals(verticalId),
      ]);
      const c = (cfgRes.config || {}) as Record<string, unknown>;
      const g = (goalsRes.goals || {}) as Record<string, unknown>;
      setScan(toBool(c.scan_enabled, true));
      setSignals(toBool(c.signal_enabled, true));
      setLive(toBool(c.live_execution_enabled, true));
      setEngine(toBool(c.engine_enabled, false));
      setMaxLoss(g.max_daily_loss_usd != null && g.max_daily_loss_usd !== '' ? String(g.max_daily_loss_usd) : '');
      setBudgetPct(
        g.daily_budget_pct_override != null && g.daily_budget_pct_override !== ''
          ? String(g.daily_budget_pct_override)
          : '',
      );
      setProfitPct(
        g.profit_target_pct_override != null && g.profit_target_pct_override !== ''
          ? String(g.profit_target_pct_override)
          : '',
      );
      setNotes(typeof g.notes === 'string' ? g.notes : '');
      const ws = (c.war_room_schedule || {}) as Record<string, unknown>;
      setWrSchedule(ws.schedule_enabled === true);
      setWrTime(typeof ws.run_time_hhmm === 'string' && ws.run_time_hhmm.trim() ? ws.run_time_hhmm : '06:30');
      setWrTz(typeof ws.timezone === 'string' && ws.timezone.trim() ? ws.timezone : 'America/New_York');
      setWrLastRun(ws.last_run_date != null && String(ws.last_run_date).trim() ? String(ws.last_run_date) : null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [verticalId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setErr('');
    setSavedAt(null);
    try {
      if (maxLoss.trim() !== '' && !Number.isFinite(Number(maxLoss))) {
        setErr('Max daily loss must be a valid number or empty.');
        return;
      }
      if (budgetPct.trim() !== '' && !Number.isFinite(Number(budgetPct))) {
        setErr('Daily budget % must be a valid number or empty.');
        return;
      }
      if (profitPct.trim() !== '' && !Number.isFinite(Number(profitPct))) {
        setErr('Profit target % must be a valid number or empty.');
        return;
      }
      const t = wrTime.trim();
      if (t && !/^\d{1,2}:\d{2}$/.test(t)) {
        setErr('War Room time must be HH:MM (24h), e.g. 06:30.');
        return;
      }
      const wrPatch: Record<string, unknown> = {
        schedule_enabled: wrSchedule,
        run_time_hhmm: t || '06:00',
        timezone: wrTz.trim() || 'America/New_York',
        schema_version: 1,
      };
      if (wrLastRun) wrPatch.last_run_date = wrLastRun;

      const cfgPatch: Record<string, unknown> = {
        scan_enabled: scan,
        signal_enabled: signals,
        live_execution_enabled: live,
        war_room_schedule: wrPatch,
      };
      if (verticalId === 'futures') {
        cfgPatch.engine_enabled = engine;
      }
      await verticalsApi.patchConfig(verticalId, cfgPatch);

      const goalsPatch: Record<string, unknown> = {};
      const lossNum = maxLoss.trim() === '' ? null : Number(maxLoss);
      if (maxLoss.trim() === '' || Number.isFinite(lossNum)) goalsPatch.max_daily_loss_usd = lossNum;
      const bp = budgetPct.trim() === '' ? null : Number(budgetPct);
      if (budgetPct.trim() === '' || Number.isFinite(bp)) goalsPatch.daily_budget_pct_override = bp;
      const pp = profitPct.trim() === '' ? null : Number(profitPct);
      if (profitPct.trim() === '' || Number.isFinite(pp)) goalsPatch.profit_target_pct_override = pp;
      goalsPatch.notes = notes.trim();
      await verticalsApi.patchGoals(verticalId, goalsPatch);

      setSavedAt(new Date().toISOString());
      onSaved?.();
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-8 text-muted-foreground text-sm">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading vertical controls…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Controls & goals</CardTitle>
        <CardDescription className="text-xs">
          Gates: <code className="text-[10px]">scan_enabled</code>, <code className="text-[10px]">signal_enabled</code>,{' '}
          <code className="text-[10px]">live_execution_enabled</code>
          {verticalId === 'futures' ? (
            <>
              , <code className="text-[10px]">engine_enabled</code>
            </>
          ) : null}
          . Merged into Firestore <span className="font-mono">verticals/{verticalId}</span>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 text-sm">
        {err ? <div className="text-destructive text-xs border border-destructive/30 rounded p-2">{err}</div> : null}
        {savedAt ? (
          <div className="text-xs text-emerald-600 dark:text-emerald-400">Saved at {savedAt}</div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
            <div>
              <Label htmlFor={`scan-${verticalId}`}>Scan enabled</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">Universe / engine work for this vertical.</p>
            </div>
            <Switch id={`scan-${verticalId}`} checked={scan} onCheckedChange={setScan} />
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
            <div>
              <Label htmlFor={`sig-${verticalId}`}>Signals enabled</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">Pattern evaluation may emit signals.</p>
            </div>
            <Switch id={`sig-${verticalId}`} checked={signals} onCheckedChange={setSignals} />
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
            <div>
              <Label htmlFor={`live-${verticalId}`}>Live execution</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">Broker orders when strategy allows (paper/CB still apply).</p>
            </div>
            <Switch id={`live-${verticalId}`} checked={live} onCheckedChange={setLive} />
          </div>
          {verticalId === 'futures' ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
              <div>
                <Label htmlFor={`eng-${verticalId}`}>Futures engine</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">Opt-in for futures runtime (deployment-dependent).</p>
              </div>
              <Switch id={`eng-${verticalId}`} checked={engine} onCheckedChange={setEngine} />
            </div>
          ) : null}
        </div>

        <div className="rounded-lg border p-4 space-y-4 bg-muted/20">
          <div>
            <p className="text-sm font-medium">Scheduled AI War Room (v1.1)</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              When enabled, the controller runs a scoped War Room for this asset near the local time you set (per-vertical
              dedup). Turn off to avoid automatic AI spend.
            </p>
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor={`wr-sch-${verticalId}`}>Enable schedule</Label>
            <Switch id={`wr-sch-${verticalId}`} checked={wrSchedule} onCheckedChange={setWrSchedule} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={`wr-t-${verticalId}`}>Run time (local)</Label>
              <Input id={`wr-t-${verticalId}`} value={wrTime} onChange={(e) => setWrTime(e.target.value)} placeholder="06:30" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`wr-z-${verticalId}`}>Timezone (IANA)</Label>
              <Input
                id={`wr-z-${verticalId}`}
                value={wrTz}
                onChange={(e) => setWrTz(e.target.value)}
                placeholder="America/New_York"
              />
            </div>
          </div>
          {wrLastRun ? (
            <p className="text-[11px] text-muted-foreground">
              Last scheduled run day (in this timezone): <span className="font-mono text-foreground">{wrLastRun}</span>
            </p>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`maxloss-${verticalId}`}>Max daily loss (USD)</Label>
            <Input
              id={`maxloss-${verticalId}`}
              type="number"
              step="0.01"
              placeholder="empty = no cap"
              value={maxLoss}
              onChange={(e) => setMaxLoss(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`bud-${verticalId}`}>Daily budget % override</Label>
            <Input
              id={`bud-${verticalId}`}
              type="number"
              step="0.1"
              placeholder="optional"
              value={budgetPct}
              onChange={(e) => setBudgetPct(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`prof-${verticalId}`}>Profit target % override</Label>
            <Input
              id={`prof-${verticalId}`}
              type="number"
              step="0.1"
              placeholder="optional"
              value={profitPct}
              onChange={(e) => setProfitPct(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`notes-${verticalId}`}>Notes</Label>
          <Textarea id={`notes-${verticalId}`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional operator notes" />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => void save()} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save controls & goals
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={saving}>
            Reload
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
